import React, { useCallback, useEffect, useState } from "react"

import {
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
} from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"
import { DateRange } from "react-day-picker"

import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { DateFilter } from "@/types/filters"

interface MetricsFilterProps {
  dateFilter: DateFilter
  setDateFilter: (value: DateFilter) => void
}

const MetricsFilter: React.FC<MetricsFilterProps> = ({ dateFilter, setDateFilter }) => {
  const [selectedPreset, setSelectedPreset] = useState<string>("all")
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined)

  // Update date filter when preset changes
  const handlePresetChange = useCallback(
    (preset: string) => {
      setSelectedPreset(preset)

      const now = new Date()
      let startDate: Date | undefined
      let endDate: Date = now

      switch (preset) {
        case "today":
          startDate = new Date(now.setHours(0, 0, 0, 0))
          break
        case "yesterday":
          startDate = subDays(new Date(now.setHours(0, 0, 0, 0)), 1)
          endDate = subDays(new Date(now.setHours(23, 59, 59, 999)), 1)
          break
        case "this_week":
          startDate = startOfWeek(now)
          endDate = endOfWeek(now)
          break
        case "this_month":
          startDate = startOfMonth(now)
          endDate = endOfMonth(now)
          break
        case "last_month":
          startDate = startOfMonth(subMonths(now, 1))
          endDate = endOfMonth(subMonths(now, 1))
          break
        case "all_time":
          // Set both dates to null to fetch all data
          setDateFilter({
            startDate: null,
            endDate: null,
          })
          return
        case "custom":
          // Don't set dates for custom, wait for calendar selection
          return
        case "all":
        default:
          // Default to last 30 days
          startDate = subDays(now, 30)
          break
      }

      if (startDate && endDate) {
        setDateRange({
          from: startDate,
          to: endDate,
        })

        setDateFilter({
          startDate: format(startDate, "yyyy-MM-dd"),
          endDate: format(endDate, "yyyy-MM-dd"),
        })
      }
    },
    [setDateFilter, setDateRange],
  )

  // Update date filter when custom range changes
  useEffect(() => {
    if (dateRange?.from && dateRange?.to) {
      setDateFilter({
        startDate: format(dateRange.from, "yyyy-MM-dd"),
        endDate: format(dateRange.to, "yyyy-MM-dd"),
      })
    }
  }, [dateRange, setDateFilter])

  // Initialize with default date filter on component mount
  useEffect(() => {
    if (!dateFilter.startDate && !dateFilter.endDate) {
      handlePresetChange("all")
    }
  }, [dateFilter.startDate, dateFilter.endDate, handlePresetChange])

  return (
    <div className="flex flex-wrap gap-3 items-center">
      <Select value={selectedPreset} onValueChange={handlePresetChange}>
        <SelectTrigger className="w-[180px]">
          <SelectValue placeholder="Filter by time" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Last 30 days</SelectItem>
          <SelectItem value="today">Today</SelectItem>
          <SelectItem value="yesterday">Yesterday</SelectItem>
          <SelectItem value="this_week">This week</SelectItem>
          <SelectItem value="this_month">This month</SelectItem>
          <SelectItem value="last_month">Last month</SelectItem>
          <SelectItem value="custom">Custom range</SelectItem>
        </SelectContent>
      </Select>

      {selectedPreset === "custom" && (
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4" />
              {dateRange?.from ? (
                dateRange.to ? (
                  <>
                    {format(dateRange.from, "LLL dd, y")} - {format(dateRange.to, "LLL dd, y")}
                  </>
                ) : (
                  format(dateRange.from, "LLL dd, y")
                )
              ) : (
                "Pick dates"
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              initialFocus
              mode="range"
              defaultMonth={dateRange?.from}
              selected={dateRange}
              onSelect={setDateRange}
              numberOfMonths={2}
            />
          </PopoverContent>
        </Popover>
      )}
    </div>
  )
}

export default MetricsFilter
